import { useState, useCallback, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Unlink, Download, Upload, Send, Loader2, Video, Heart, Repeat2, Eye, MessageCircle, AlertCircle, CheckCircle2, ExternalLink, Sparkles, Zap, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoginArea } from '@/components/auth/LoginArea';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useTweetVideo } from '@/hooks/useTweetVideo';
import { toast } from '@/hooks/useToast';

import { parseTwitterUrl, triggerVideoDownload, downloadVideoBlob, getProxiedVideoUrl, formatDuration, formatNumber, formatBitrate } from '@/lib/twitter';
import type { VideoVariant } from '@/lib/twitter';

const Index = () => {
  useSeoMeta({
    title: 'X Video Liberator',
    description: 'Free your videos from X/Twitter. Download, upload to Blossom, and share on Nostr.',
  });

  const { user } = useCurrentUser();

  const [urlInput, setUrlInput] = useState('');
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [publishToNostr, setPublishToNostr] = useState(false);
  const [blossomUrl, setBlossomUrl] = useState<string | null>(null);
  const [isUploadingToBlossom, setIsUploadingToBlossom] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [published, setPublished] = useState(false);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [blobFetchFailed, setBlobFetchFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: tweetData, isLoading: isFetching, error: fetchError } = useTweetVideo(submittedUrl);
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();

  const handleSubmit = useCallback(() => {
    const parsed = parseTwitterUrl(urlInput);
    if (!parsed) {
      toast({ title: 'Invalid URL', description: 'Please enter a valid X/Twitter post URL.', variant: 'destructive' });
      return;
    }
    setSubmittedUrl(urlInput.trim());
    setVideoBlob(null);
    setBlossomUrl(null);
    setPublished(false);
    setNoteText('');
    setPublishToNostr(false);
    setDownloadTriggered(false);
    setUploadProgress(0);
    setSelectedVariantIndex(0);
    setBlobFetchFailed(false);
  }, [urlInput]);

  const handleReset = useCallback(() => {
    setUrlInput('');
    setSubmittedUrl(null);
    setVideoBlob(null);
    setBlossomUrl(null);
    setPublished(false);
    setNoteText('');
    setPublishToNostr(false);
    setDownloadTriggered(false);
    setUploadProgress(0);
    setIsUploadingToBlossom(false);
    setSelectedVariantIndex(0);
    setBlobFetchFailed(false);
  }, []);

  const getSelectedVariant = useCallback((): VideoVariant | undefined => {
    if (!tweetData || tweetData.videos.length === 0) return undefined;
    const variants = tweetData.videos[0].variants;
    return variants[selectedVariantIndex] ?? variants[0];
  }, [tweetData, selectedVariantIndex]);

  const handleDownload = useCallback(() => {
    if (!tweetData || tweetData.videos.length === 0) return;
    const variant = getSelectedVariant();
    if (!variant) return;

    const quality = variant.qualityLabel ? `_${variant.qualityLabel}` : '';
    const filename = `x_video_${tweetData.author.screenName}${quality}_${Date.now()}.mp4`;
    triggerVideoDownload(variant.url, filename);
    setDownloadTriggered(true);
    toast({ title: 'Download started!', description: 'Check your browser downloads.' });
  }, [tweetData, getSelectedVariant]);

  const uploadBlobToBlossom = useCallback(async (blob: Blob) => {
    if (!tweetData) return;
    const variant = getSelectedVariant();

    setUploadProgress(40);
    const quality = variant?.qualityLabel ? `_${variant.qualityLabel}` : '';
    const file = new File([blob], `x_video_${tweetData.author.screenName}${quality}_${Date.now()}.mp4`, { type: 'video/mp4' });

    setUploadProgress(60);
    const tags = await uploadFile(file);
    const url = tags[0][1];

    setUploadProgress(100);
    setBlossomUrl(url);
    toast({ title: 'Upload complete!', description: 'Video uploaded to your Blossom server.' });
  }, [tweetData, uploadFile, getSelectedVariant]);

  const handleUploadToBlossom = useCallback(async () => {
    if (!tweetData || !user) return;
    const variant = getSelectedVariant();
    if (!variant) return;

    setIsUploadingToBlossom(true);
    setUploadProgress(10);
    setBlobFetchFailed(false);

    try {
      let blob = videoBlob;
      if (!blob) {
        setUploadProgress(20);
        try {
          blob = await downloadVideoBlob(
            variant.url,
            undefined,
            (loaded, total) => {
              if (total > 0) {
                setUploadProgress(20 + Math.round((loaded / total) * 20));
              }
            },
          );
          setVideoBlob(blob);
        } catch (fetchErr) {
          console.warn('Failed to fetch video for Blossom upload:', fetchErr);
          setBlobFetchFailed(true);
          setIsUploadingToBlossom(false);
          toast({
            title: 'Download the video first',
            description: 'Twitter blocked the automatic download. Use the Download button above, then select the file to upload.',
          });
          return;
        }
      }
      await uploadBlobToBlossom(blob);
    } catch (err) {
      toast({ title: 'Upload failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsUploadingToBlossom(false);
    }
  }, [tweetData, user, videoBlob, uploadFile, getSelectedVariant, uploadBlobToBlossom]);

  const handleManualFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tweetData) return;
    e.target.value = '';

    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }

    setIsUploadingToBlossom(true);
    setUploadProgress(30);

    try {
      const blob = new Blob([await file.arrayBuffer()], { type: 'video/mp4' });
      setVideoBlob(blob);
      await uploadBlobToBlossom(blob);
      setBlobFetchFailed(false);
    } catch (err) {
      toast({ title: 'Upload failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsUploadingToBlossom(false);
    }
  }, [tweetData, uploadBlobToBlossom]);

  const handlePublishNote = useCallback(async () => {
    if (!blossomUrl || !user) return;

    try {
      const content = noteText ? `${noteText}\n\n${blossomUrl}` : blossomUrl;
      const video = tweetData?.videos[0];
      const imetaParts = [`url ${blossomUrl}`, 'm video/mp4'];
      if (video) {
        imetaParts.push(`dim ${video.width}x${video.height}`);
      }

      await publishEvent({
        kind: 1,
        content,
        tags: [
          ['imeta', ...imetaParts],
          ['r', blossomUrl],
        ],
      });

      setPublished(true);
      toast({ title: 'Published!', description: 'Your note has been published to Nostr.' });
    } catch (err) {
      toast({ title: 'Publish failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    }
  }, [blossomUrl, noteText, user, tweetData, publishEvent]);

  const video = tweetData?.videos[0];
  const variants = video?.variants ?? [];

  const getVariantLabel = (v: VideoVariant) => {
    const parts: string[] = [];
    if (v.qualityLabel) parts.push(v.qualityLabel);
    if (v.bitrate) parts.push(formatBitrate(v.bitrate));
    return parts.join(' · ') || 'Unknown quality';
  };

  return (
    <div className="min-h-screen relative">
      {/* Hero background image — only when no video extracted yet */}
      {!submittedUrl && (
        <div className="fixed inset-0 -z-10 pointer-events-none">
          <img
            src="/hero-bg.webp"
            alt=""
            className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl object-cover opacity-30 dark:opacity-25 mask-image-fade"
            style={{
              maskImage: 'radial-gradient(ellipse 60% 50% at 50% 30%, black, transparent 80%)',
              WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 30%, black, transparent 80%)',
            }}
          />
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/60 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 text-white shadow-lg shadow-purple-500/25">
              <Unlink className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight tracking-tight">X Video Liberator</h1>
              <p className="text-xs text-muted-foreground leading-none hidden sm:block">Free your videos</p>
            </div>
          </div>
          <LoginArea className="max-w-60" />
        </div>
      </header>

      <main className="container py-8 space-y-8 max-w-3xl mx-auto">
        {/* Hero section */}
        {!submittedUrl && (
          <div className="text-center space-y-5 pt-6 pb-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-500/20 bg-purple-500/10 px-4 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400">
              <Sparkles className="h-3.5 w-3.5" />
              Powered by Nostr & Blossom
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              Liberate videos from{' '}
              <span className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 bg-clip-text text-transparent">X/Twitter</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
              Download to your device, upload to Blossom, and share on Nostr. Your media, your control.
            </p>
          </div>
        )}

        {/* URL Input */}
        <Card className="border-border/60 shadow-xl shadow-purple-500/5">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="Paste an X/Twitter post URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                className="flex-1 h-12 text-base border-border/60 focus-visible:border-purple-500/50 focus-visible:ring-purple-500/20"
                disabled={isFetching}
              />
              {submittedUrl ? (
                <Button variant="outline" size="lg" onClick={handleReset} className="h-12 px-6">
                  Clear
                </Button>
              ) : (
                <Button size="lg" onClick={handleSubmit} disabled={!urlInput.trim() || isFetching} className="h-12 px-6 bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white border-0 shadow-lg shadow-purple-500/25">
                  {isFetching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Video className="h-5 w-5" />}
                  <span className="ml-2 hidden sm:inline">Extract</span>
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Supports twitter.com, x.com, and mobile links
            </p>
          </CardContent>
        </Card>

        {/* Loading state */}
        {isFetching && (
          <Card className="border-border/60 shadow-xl shadow-purple-500/5">
            <CardContent className="pt-6 space-y-4">
              <div className="flex gap-4">
                <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-48 w-full rounded-lg" />
              <div className="flex gap-4">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {fetchError && (
          <Card className="border-destructive/40 shadow-lg shadow-destructive/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="font-medium text-destructive">Failed to extract video</p>
                  <p className="text-sm text-muted-foreground mt-1">{fetchError.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Video Preview */}
        {tweetData && video && (
          <div className="space-y-6">
            {/* Tweet info */}
            <Card className="border-border/60 shadow-xl shadow-purple-500/5 overflow-hidden">
              <CardContent className="pt-6 space-y-4">
                {/* Author row */}
                <div className="flex items-center gap-3">
                  {tweetData.author.avatarUrl && (
                    <img
                      src={tweetData.author.avatarUrl}
                      alt={tweetData.author.name}
                      className="h-10 w-10 rounded-full object-cover ring-2 ring-purple-500/20"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{tweetData.author.name}</p>
                    <p className="text-xs text-muted-foreground">@{tweetData.author.screenName}</p>
                  </div>
                  <a
                    href={tweetData.tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-purple-500 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                {/* Tweet text */}
                {tweetData.text && (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{tweetData.text}</p>
                )}

                {/* Video player */}
                <div className="relative rounded-xl overflow-hidden bg-black ring-1 ring-purple-500/10">
                  <video
                    src={getProxiedVideoUrl(video.url)}
                    poster={video.thumbnailUrl}
                    controls
                    className="w-full max-h-[500px] object-contain"
                    preload="metadata"
                  />
                </div>

                {/* Video metadata */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/15">
                    {video.width}x{video.height}
                  </Badge>
                  {video.duration > 0 && (
                    <Badge variant="secondary" className="bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/15">
                      {formatDuration(video.duration)}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/15">
                    {video.type === 'gif' ? 'GIF' : 'MP4'}
                  </Badge>
                  {variants.length > 0 && variants[0].bitrate && (
                    <Badge variant="secondary" className="bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/15">
                      Best: {formatBitrate(variants[0].bitrate)}
                    </Badge>
                  )}
                </div>

                {/* Engagement stats */}
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-1 border-t border-border/40">
                  {tweetData.likes > 0 && (
                    <span className="flex items-center gap-1.5 pt-3">
                      <Heart className="h-3.5 w-3.5" />
                      {formatNumber(tweetData.likes)}
                    </span>
                  )}
                  {tweetData.retweets > 0 && (
                    <span className="flex items-center gap-1.5 pt-3">
                      <Repeat2 className="h-3.5 w-3.5" />
                      {formatNumber(tweetData.retweets)}
                    </span>
                  )}
                  {tweetData.replies > 0 && (
                    <span className="flex items-center gap-1.5 pt-3">
                      <MessageCircle className="h-3.5 w-3.5" />
                      {formatNumber(tweetData.replies)}
                    </span>
                  )}
                  {tweetData.views > 0 && (
                    <span className="flex items-center gap-1.5 pt-3">
                      <Eye className="h-3.5 w-3.5" />
                      {formatNumber(tweetData.views)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="border-border/60 shadow-xl shadow-purple-500/5">
              <CardContent className="pt-6 space-y-6">
                {/* Download section */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Download className="h-4 w-4 text-purple-500" />
                    Download Video
                  </h3>

                  {/* Quality selector */}
                  {variants.length > 1 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Quality</Label>
                      <Select
                        value={String(selectedVariantIndex)}
                        onValueChange={(v) => {
                          setSelectedVariantIndex(Number(v));
                          setVideoBlob(null);
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {variants.map((v, i) => (
                            <SelectItem key={i} value={String(i)}>
                              {getVariantLabel(v)}
                              {i === 0 ? ' (Best)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button
                    onClick={handleDownload}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white border-0 shadow-lg shadow-purple-500/25"
                    size="lg"
                  >
                    {downloadTriggered ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Download Again
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download MP4
                      </>
                    )}
                  </Button>
                </div>

                {/* Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border/60" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground tracking-wider">Share on Nostr</span>
                  </div>
                </div>

                {/* Blossom + Nostr section */}
                {!user ? (
                  <div className="text-center space-y-3 py-4">
                    <p className="text-sm text-muted-foreground">Sign in to upload to Blossom and share on Nostr</p>
                    <LoginArea className="justify-center" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Upload to Blossom */}
                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Upload className="h-4 w-4 text-purple-500" />
                        Upload to Blossom
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Upload the video to your Blossom media server for permanent hosting.
                      </p>

                      {isUploadingToBlossom && (
                        <div className="space-y-2">
                          <Progress value={uploadProgress} className="h-2" />
                          <p className="text-xs text-muted-foreground text-center">
                            {uploadProgress < 40 ? 'Downloading video...' : uploadProgress < 60 ? 'Preparing upload...' : uploadProgress < 100 ? 'Uploading to Blossom...' : 'Complete!'}
                          </p>
                        </div>
                      )}

                      {/* Hidden file input for manual upload fallback */}
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleManualFileUpload}
                      />

                      {blossomUrl ? (
                        <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm font-medium">Uploaded successfully!</span>
                          </div>
                          <p className="text-xs text-muted-foreground break-all font-mono">{blossomUrl}</p>
                        </div>
                      ) : blobFetchFailed ? (
                        <div className="space-y-3">
                          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                              Twitter blocked the automatic video download. Download the video using the button above, then select the downloaded file below.
                            </p>
                          </div>
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingToBlossom}
                            variant="outline"
                            className="w-full"
                            size="lg"
                          >
                            {isUploadingToBlossom ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Select Downloaded Video File
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={handleUploadToBlossom}
                          disabled={isUploadingToBlossom}
                          variant="outline"
                          className="w-full hover:bg-purple-500/5 hover:border-purple-500/30"
                          size="lg"
                        >
                          {isUploadingToBlossom ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload to Blossom
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Publish to Nostr */}
                    {blossomUrl && (
                      <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="publish-nostr" className="cursor-pointer">
                            <Send className="h-4 w-4 text-purple-500" />
                            Publish to Nostr
                          </Label>
                          <Switch
                            id="publish-nostr"
                            checked={publishToNostr}
                            onCheckedChange={setPublishToNostr}
                          />
                        </div>

                        {publishToNostr && (
                          <div className="space-y-3">
                            <Textarea
                              placeholder="Add a message to your note (optional)..."
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              className="min-h-24 border-border/60 focus-visible:border-purple-500/50 focus-visible:ring-purple-500/20"
                            />

                            {/* Preview */}
                            <div className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">Note preview:</p>
                              <p className="text-sm whitespace-pre-wrap">
                                {noteText ? `${noteText}\n\n` : ''}{blossomUrl}
                              </p>
                            </div>

                            {published ? (
                              <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm font-medium">Published to Nostr!</span>
                              </div>
                            ) : (
                              <Button
                                onClick={handlePublishNote}
                                disabled={isPublishing}
                                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white border-0 shadow-lg shadow-purple-500/25"
                                size="lg"
                              >
                                {isPublishing ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Publishing...
                                  </>
                                ) : (
                                  <>
                                    <Send className="h-4 w-4 mr-2" />
                                    Publish Note
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty state when no URL submitted */}
        {!submittedUrl && !isFetching && (
          <div className="space-y-8">
            <Card className="border-dashed border-purple-500/20 bg-card/50 backdrop-blur-sm">
              <CardContent className="py-14 px-8 text-center space-y-5">
                <div className="flex justify-center">
                  <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500/15 to-purple-700/15 flex items-center justify-center shadow-inner">
                    <Video className="h-10 w-10 text-purple-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-xl">Paste an X/Twitter URL above</h3>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed">
                    Paste any X or Twitter post URL that contains a video. We&apos;ll extract the highest quality version for you to download, upload to Blossom, and share on Nostr.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 pt-2">
                  <Badge variant="outline" className="border-purple-500/20 text-purple-600 dark:text-purple-400">Download MP4</Badge>
                  <Badge variant="outline" className="border-purple-500/20 text-purple-600 dark:text-purple-400">Upload to Blossom</Badge>
                  <Badge variant="outline" className="border-purple-500/20 text-purple-600 dark:text-purple-400">Share on Nostr</Badge>
                </div>
              </CardContent>
            </Card>

            {/* How it works */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="text-center space-y-3 p-5 rounded-2xl border border-border/40 bg-card/30 backdrop-blur-sm transition-colors hover:border-purple-500/20 hover:bg-purple-500/5">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500/15 to-purple-700/15 flex items-center justify-center mx-auto">
                  <Download className="h-6 w-6 text-violet-500" />
                </div>
                <h4 className="font-semibold text-sm">1. Download</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Get the highest quality MP4 directly to your device</p>
              </div>
              <div className="text-center space-y-3 p-5 rounded-2xl border border-border/40 bg-card/30 backdrop-blur-sm transition-colors hover:border-purple-500/20 hover:bg-purple-500/5">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-fuchsia-500/15 to-purple-700/15 flex items-center justify-center mx-auto">
                  <Upload className="h-6 w-6 text-fuchsia-500" />
                </div>
                <h4 className="font-semibold text-sm">2. Upload</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Optionally upload to your Blossom media server</p>
              </div>
              <div className="text-center space-y-3 p-5 rounded-2xl border border-border/40 bg-card/30 backdrop-blur-sm transition-colors hover:border-purple-500/20 hover:bg-purple-500/5">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500/15 to-violet-700/15 flex items-center justify-center mx-auto">
                  <Send className="h-6 w-6 text-purple-500" />
                </div>
                <h4 className="font-semibold text-sm">3. Share</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Publish a note on Nostr with the video embedded</p>
              </div>
            </div>

            {/* Feature highlights */}
            <div className="grid gap-4 sm:grid-cols-3 pt-2">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10 flex-shrink-0">
                  <Zap className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm font-medium">Instant extraction</p>
                  <p className="text-xs text-muted-foreground">Multiple quality options</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10 flex-shrink-0">
                  <Shield className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm font-medium">No tracking</p>
                  <p className="text-xs text-muted-foreground">No ads, no data collection</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card/30 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10 flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm font-medium">Nostr native</p>
                  <p className="text-xs text-muted-foreground">Decentralized publishing</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8 mt-12">
        <div className="container text-center text-xs text-muted-foreground space-y-2">
          <p>
            Vibed with{' '}
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-500 transition-colors">
              Shakespeare
            </a>
          </p>
          <p>
            Powered by Twitter Syndication API &amp;{' '}
            <a href="https://github.com/FxEmbed/FxEmbed" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-500 transition-colors">
              FxTwitter
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
